import { tz } from "@date-fns/tz";
import { format } from "date-fns";
import { CalendarIcon, XIcon } from "lucide-react";
import * as React from "react";
import type { Matcher } from "react-day-picker";
import type { Control, FieldValues, Path } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toUtcCalendarIso } from "@/utils/formatIsoToUTC";

interface DatePickerControlProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: React.ReactNode;
  placeholder?: string;
  classes?: string;
  disabledDays?: Matcher | Matcher[];
  formatValue?: (value: string) => string;
  clearable?: boolean;
  disabled?: boolean;
  timeZone?: string;
}

export const DatePickerControl = <T extends FieldValues>({
  control,
  name,
  label,
  placeholder = "Pick a date",
  classes,
  disabledDays,
  formatValue,
  clearable = false,
  disabled = false,
  timeZone = "UTC",
}: DatePickerControlProps<T>) => {
  const [open, setOpen] = React.useState(false);
  const zone = tz(timeZone);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("flex flex-col w-full relative", classes)}>
          <FormLabel>{label}</FormLabel>
          <Popover
            open={open && !disabled}
            onOpenChange={nextOpen => {
              if (!disabled) setOpen(nextOpen);
            }}
          >
            <div className="relative">
              <FormControl>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    className={cn(
                      "w-full justify-start rounded-[6px] h-10 shadow-none font-normal",
                      !field.value && "text-muted-foreground",
                      clearable && field.value && "pr-10"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {field.value ? (
                      formatValue ? (
                        formatValue(String(field.value))
                      ) : (
                        format(new Date(field.value), "PPP", { in: zone })
                      )
                    ) : (
                      <span className="text-gray-400">{placeholder}</span>
                    )}
                  </Button>
                </PopoverTrigger>
              </FormControl>

              {clearable && field.value && !disabled && (
                <button
                  type="button"
                  aria-label="Clear date"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    field.onChange("");
                    setOpen(false);
                  }}
                >
                  <XIcon className="size-4" />
                </button>
              )}
            </div>

            <PopoverContent align="start" className="w-auto p-0">
              <Calendar
                mode="single"
                timeZone={timeZone}
                selected={field.value ? new Date(field.value) : undefined}
                defaultMonth={field.value ? new Date(field.value) : undefined}
                disabled={disabledDays}
                onSelect={date => {
                  if (!date) return;
                  field.onChange(toUtcCalendarIso(date));
                  setOpen(false);
                }}
                className="border rounded-md"
              />
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
